import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { requirementsApi } from '../../services/api';
// #383 strand 2: the picker now renders the same RequirementsTree navigator the main
// view uses (single-select), instead of the legacy BaseTreeView/RichTreeView
// (@mui/x-tree-view). #413: 'all' (relationship/move target) mode is LAZY — bounded
// root page, children on expand, Load-more nodes, open-to-current-node, and
// SERVER-side search — via usePickerTree. 'containers' mode stays eager on purpose
// (issue #413 decision (b): containers are a handful of folders; there is no lazy
// containers endpoint and eager is fine at that size).
import RequirementsTree from '../requirements/RequirementsTree';
import usePickerTree from '../../hooks/usePickerTree';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * UnifiedContainerSelector component - Reusable dialog for selecting containers
 * 
 * @component
 */
const UnifiedContainerSelector = ({
  open,
  onClose,
  onSelect,
  showCreateOption = false,
  title = "Select Container",
  fallbackToLegacy = false,
  // 'containers' (default) lists only container-type items; 'all' lists the
  // full item tree so a relationship can target any requirement, not just a
  // container (otherwise projects with no containers show "No containers found").
  selectMode = 'containers',
  // #375: when picking a relationship target, scope the tree to the editing
  // item's project — relationships can only be formed WITHIN a project, so
  // offering items from other projects (or other tenants) is both wrong and a
  // cross-project data-exposure surface. null => unscoped (legacy/container mode).
  projectId = null,
  // #408: ancestor ids to pre-expand so the picker opens showing the item's CURRENT
  // location (e.g. the "Move…" flow opens with the item's parent chain already open).
  initialExpandedIds = EMPTY_ARRAY,
}) => {
  const selectingAll = selectMode === 'all';
  const noun = selectingAll ? 'items' : 'containers';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]); // Initialize as array
  const [expanded, setExpanded] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newContainerName, setNewContainerName] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // #413: lazy tree state for 'all' mode (bounded pages + children-on-expand +
  // server search + open-to-current-node). Unconditional hook; only used when
  // selectingAll. #375 project scoping rides on every request it makes.
  const picker = usePickerTree({ projectId });
  const searchDebounce = useRef(null);

  // Latest-value refs for the three things the open/search effects below CALL but must
  // not re-run on: `picker` is a fresh object literal from usePickerTree on every render,
  // `initialExpandedIds` is a prop array literal from the caller, and `loadContainers` is
  // a plain (unmemoized) function declared in this body. Listing any of them would make
  // "the dialog opened" fire on every render — i.e. a fetch pump behind an open modal.
  const pickerRef = useRef(picker);
  const initialExpandedIdsRef = useRef(initialExpandedIds);
  const loadContainersRef = useRef(null);
  useEffect(() => {
    pickerRef.current = picker;
    initialExpandedIdsRef.current = initialExpandedIds;
    loadContainersRef.current = loadContainers;
  });

  // Load when dialog opens
  useEffect(() => {
    if (open) {
      // Reset selection + search when dialog opens
      setSelected([]);
      setSearchTerm('');
      if (selectingAll) {
        // Bounded first page, then open the moved/linked item's ancestor chain
        // (#408) via a real lazy branch load.
        const p = pickerRef.current;
        p.loadRoots().then(() => p.openTo(initialExpandedIdsRef.current));
      } else {
        loadContainersRef.current();
      }
    }
  }, [open, projectId, selectingAll]);

  // #413: server-side search spans the WHOLE (unloaded) tree — debounced.
  useEffect(() => {
    if (!open || !selectingAll) return undefined;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => pickerRef.current.search(searchTerm), 250);
    return () => clearTimeout(searchDebounce.current);
  }, [searchTerm, open, selectingAll]);

  // Load containers from API ('containers' mode — deliberately eager, see above)
  const loadContainers = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log(`UnifiedContainerSelector: Loading ${noun}...`);

      // Try to get containers
      let data = [];

      try {
        // First try the dedicated containers API
        data = await requirementsApi.getContainers();
        console.log('getContainers returned:', Array.isArray(data) ? `${data.length} items` : typeof data);
      } catch (containerErr) {
        console.error("getContainers failed, falling back to getItems:", containerErr);
        
        // Only fall back to getItems if fallbackToLegacy is true
        if (fallbackToLegacy) {
          // Fall back to getItems and filter for container type
          const allItems = await requirementsApi.getItems(true);
          console.log('getItems returned:', Array.isArray(allItems) ? `${allItems.length} items` : typeof allItems);
          
          if (Array.isArray(allItems)) {
            // Filter containers at root level. API-created containers carry the
            // top-level `type` field; older data may use `attributes.type`.
            data = allItems.filter(item => item &&
              (item.type === 'container' || item.attributes?.type === 'container'));
            console.log(`Filtered ${data.length} containers from items`);
          }
        } else {
          // If no fallback, re-throw the error
          throw containerErr;
        }
      }
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Container data is not an array:', data);
        data = [];
      }
      
      // Process the data to ensure all items have valid IDs
      const processedData = ensureUniqueIds(data);
      console.log(`Processed ${processedData.length} containers`);
      
      setItems(processedData);

      // Auto-expand top level containers, PLUS the moved item's ancestor chain (#408)
      // so the picker opens revealing where the item currently lives.
      const baseTop = (processedData.length > 0 && processedData.length < 10)
        ? processedData.map(item => item.id) : [];
      setExpanded(Array.from(new Set([...baseTop, ...initialExpandedIds])));
    } catch (err) {
      console.error("Failed to load containers:", err);
      setError(err.message || "Failed to load containers");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to ensure all items have unique IDs
  const ensureUniqueIds = (list) => {
    if (!Array.isArray(list)) return [];
    
    const seenIds = new Set();
    
    // Recursive function to process each item
    const processItem = (item, path = '') => {
      if (!item) return null;
      
      // Assign a unique ID if missing or duplicate
      if (!item.id || seenIds.has(item.id)) {
        // Create a unique ID based on title/path or a random string
        const uniqueId = item.title ? 
          `container-${path}-${item.title.replace(/\s+/g, '-').toLowerCase()}` : 
          `container-${Math.random().toString(36).substring(2, 9)}`;
        
        // Keep the original ID in a different property if it exists
        if (item.id) {
          item.originalId = item.id;
        }
        
        item.id = uniqueId;
      }
      
      // Add this ID to seen set
      seenIds.add(item.id);
      
      // Process children recursively if array
      if (Array.isArray(item.children)) {
        item.children = item.children
          .map((child, index) => processItem(child, `${path}-${index}`))
          .filter(Boolean); // Remove any null items
      } else {
        // Ensure children is always an array
        item.children = [];
      }
      
      return item;
    };
    
    // Process all top-level items
    return list.map((item, index) => processItem(item, `${index}`)) || [];
  };

  // Handle selection change
  const handleSelectedChange = (event, nodeIds) => {
    console.log('Selection changed:', nodeIds);
    // Ensure nodeIds is always treated as an array
    setSelected(Array.isArray(nodeIds) ? nodeIds : [nodeIds].filter(Boolean));
  };

  // Handle expanded change
  const handleExpandedChange = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  // Handle create new container
  const handleCreateContainer = async () => {
    if (!newContainerName.trim()) {
      setError('Container name is required');
      return;
    }
    
    setLoading(true);
    
    try {
      const parentId = selected.length === 1 ? selected[0] : null;
      
      const newContainer = await requirementsApi.createItem({
        title: newContainerName.trim(),
        description: newContainerDescription,
        attributes: {
          type: 'container'
        },
        parent_id: parentId
      });
      
      // Call onSelect with new container
      onSelect(newContainer.id, newContainer.title);
      
    } catch (err) {
      console.error('Failed to create container:', err);
      setError(`Failed to create container: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Effective data source: the lazy picker state in 'all' mode, the eager
  // container load otherwise (#413).
  const effItems = selectingAll ? picker.items : items;
  const effLoading = selectingAll ? picker.loading : loading;
  const effError = selectingAll ? picker.error : error;

  // Handle select button click
  const handleSelect = () => {
    if (selected.length === 1) {
      const selectedItem = findItemById(effItems, selected[0]);
      if (selectedItem) {
        // Use originalId if it exists, otherwise use the assigned id
        const idToReturn = selectedItem.originalId || selectedItem.id;
        
        // Log details for debugging
        console.log('Container selected:', {
          id: idToReturn,
          title: selectedItem.title || 'Unnamed Container',
          selected: selected
        });
        
        onSelect(idToReturn, selectedItem.title || 'Unnamed Container');
      } else {
        console.error('Selected item not found:', selected[0]);
      }
    } else {
      console.warn('No selection or multiple items selected:', selected);
    }
  };
  
  // Helper function to find item by ID
  const findItemById = (list, id) => {
    if (!list || !id) return null;

    for (const item of list) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { height: '70vh', display: 'flex', flexDirection: 'column' } },
      }}
    >
      <DialogTitle>
        {showCreateForm ? 'Create New Container' : title}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {showCreateForm ? (
          <Box sx={{ p: 2 }}>
            <TextField
              label="Container Name"
              fullWidth
              value={newContainerName}
              onChange={(e) => setNewContainerName(e.target.value)}
              margin="normal"
              error={!newContainerName.trim()}
              helperText={!newContainerName.trim() ? 'Name is required' : ''}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={4}
              value={newContainerDescription}
              onChange={(e) => setNewContainerDescription(e.target.value)}
              margin="normal"
            />
          </Box>
        ) : (
          <>
            {effLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : effError ? (
              <Typography sx={{ p: 2, color: 'error' }}>Error: {effError}</Typography>
            ) : effItems.length === 0 && !searchTerm ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>No {noun} found.</Typography>
                {showCreateOption && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateForm(true)}
                    sx={{ mt: 2 }}
                  >
                    Create New Container
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${noun}…`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 1 }}
                  slotProps={{
                    htmlInput: { 'data-testid': 'container-search' },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                      ),
                    },
                  }}
                />
                <Box sx={{ flex: 1, minHeight: 0, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, overflow: 'auto' }}>
                  {selectingAll ? (
                    // #413: LAZY picker — children fetched on expand, Load-more
                    // pagination nodes, server-side search results rendered as a
                    // pruned tree (same props the main tree uses for its own
                    // lazy/search modes).
                    <RequirementsTree
                      items={effItems}
                      selected={selected}
                      expanded={picker.expanded}
                      filterText=""
                      searchActive={picker.searchActive}
                      loadingIds={picker.loadingIds}
                      singleSelect
                      onSelectedChange={(ids) => handleSelectedChange(null, ids)}
                      onExpandedChange={(ids) => picker.setExpanded(ids)}
                      onLoadChildren={picker.loadChildren}
                      onLoadMore={picker.loadMore}
                    />
                  ) : (
                    // 'containers' mode: eager by design (few folders) — client
                    // filterText search surfaces matches at any depth.
                    <RequirementsTree
                      items={items}
                      selected={selected}
                      expanded={expanded}
                      filterText={searchTerm}
                      eager
                      singleSelect
                      onSelectedChange={(ids) => handleSelectedChange(null, ids)}
                      onExpandedChange={(ids) => handleExpandedChange(null, ids)}
                    />
                  )}
                </Box>
              </Box>
            )}
            
            {/* Option to create new container (when containers exist) */}
            {!effLoading && !effError && effItems.length > 0 && showCreateOption && (
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create New Container
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        {showCreateForm ? (
          <>
            <Button onClick={() => setShowCreateForm(false)}>Back</Button>
            <Button
              onClick={handleCreateContainer}
              variant="contained"
              color="primary"
              disabled={!newContainerName.trim()}
            >
              Create
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSelect}
              variant="contained"
              color="primary"
              disabled={selected.length !== 1}
            >
              Select
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

UnifiedContainerSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  showCreateOption: PropTypes.bool,
  title: PropTypes.string,
  fallbackToLegacy: PropTypes.bool,
  selectMode: PropTypes.oneOf(['containers', 'all']),
  projectId: PropTypes.string,
  initialExpandedIds: PropTypes.arrayOf(PropTypes.string),
};

export default UnifiedContainerSelector;
